import { useState } from "react";
import { CalendarDays, FilePenLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    Button,
    cn,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Eyebrow,
    StatusBadge,
    Text,
} from "@athena/ui";
import { CHANNEL_ICON, CHANNEL_TONE } from "../helpers/channelIcon";
import type { ClientRecord, ContactEntry } from "../helpers/client";
import {
    CONTACT_OUTCOME_VARIANT,
    formatCalendarDate,
    formatClockTime,
    formatDay,
    fullName,
    parseCalendarDate,
} from "../helpers/client";
import { LogContactForm, type LoggedContact } from "./LogContactForm";
import { DeleteDialog } from "@/components/DeleteDialog";

export interface ContactLogDialogEntry {
    contact: ContactEntry;
    client: ClientRecord;
}

interface ContactLogDialogProps {
    entry: ContactLogDialogEntry | undefined;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    today: Date;
    onSave: (entry: ContactLogDialogEntry, edited: LoggedContact) => Promise<void>;
    onDelete: (entry: ContactLogDialogEntry) => Promise<void>;
}

type Mode = "view" | "edit" | "delete";

export function ContactLogDialog({ entry, open, onOpenChange, today, onSave, onDelete }: ContactLogDialogProps) {
    const { t } = useTranslation();
    const [
        mode,
        setMode,
    ] = useState<Mode>("view");
    const [
        deleting,
        setDeleting,
    ] = useState<boolean>(false);

    const changeOpen = (next: boolean) => {
        if (!next) setMode("view");
        onOpenChange(next);
    };

    const confirmDelete = async (shown: ContactLogDialogEntry) => {
        setDeleting(true);
        try {
            await onDelete(shown);
            changeOpen(false);
        } catch {
            // The caller has said what went wrong; the confirmation stays up so it can be tried again.
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={changeOpen}>
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
                    {entry ? (
                        mode === "edit" ? (
                            <EditContact
                                entry={entry}
                                today={today}
                                onCancel={() => setMode("view")}
                                onSave={async (edited) => {
                                    await onSave(entry, edited);
                                    setMode("view");
                                }}
                            />
                        ) : (
                            <ContactDetails
                                entry={entry}
                                onClose={() => changeOpen(false)}
                                onEdit={() => setMode("edit")}
                                onDelete={() => setMode("delete")}
                            />
                        )
                    ) : null}
                </DialogContent>
            </Dialog>

            {entry ? (
                <DeleteDialog
                    open={open && mode === "delete"}
                    onOpenChange={(next) => {
                        if (!next && !deleting) setMode("view");
                    }}
                    title={t("admin.alumni.contactLog.detail.deleteTitle")}
                    description={t("admin.alumni.contactLog.detail.deleteDescription", {
                        channel: t(`admin.alumni.channel.${entry.contact.channel}`).toLowerCase(),
                        name: fullName(entry.client),
                        firstName: entry.client.firstName,
                        date: formatDay(new Date(entry.contact.contactedAt)),
                        time: formatClockTime(entry.contact.contactedAt),
                    })}
                    confirmLabel={t("admin.alumni.contactLog.detail.deleteConfirm")}
                    loading={deleting}
                    onConfirm={() => confirmDelete(entry)}
                />
            ) : null}
        </>
    );
}

const useSummary = ({ contact, client }: ContactLogDialogEntry) => {
    const { t } = useTranslation();
    return t("admin.alumni.contactLog.detail.summary", {
        date: formatDay(new Date(contact.contactedAt)),
        time: formatClockTime(contact.contactedAt),
        name: fullName(client),
    });
};

function ContactDetails({
    entry,
    onEdit,
    onDelete,
}: {
    entry: ContactLogDialogEntry;
    onClose: () => void;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const { t } = useTranslation();
    const { contact, client } = entry;
    const ChannelIcon = CHANNEL_ICON[contact.channel];
    const summary = useSummary(entry);

    return (
        <>
            <DialogHeader className="border-border -mx-6 flex-row items-center gap-4 border-b px-6 pb-5 text-left">
                <span
                    className={cn(
                        "flex h-12 w-12 shrink-0 items-center justify-center rounded-full",
                        CHANNEL_TONE[contact.channel],
                    )}
                >
                    <ChannelIcon className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0">
                    <div className="row flex items-center gap-4">
                        <DialogTitle>{t(`admin.alumni.channel.${contact.channel}`)}</DialogTitle>
                        <StatusBadge
                            variant={CONTACT_OUTCOME_VARIANT[contact.outcome]}
                            className="border-transparent lowercase"
                        >
                            {t(`admin.alumni.outcome.${contact.outcome}`)}
                        </StatusBadge>
                    </div>
                    <DialogDescription className="mt-1">{summary}</DialogDescription>
                </div>
            </DialogHeader>
            <div className="flex flex-col gap-5 pt-1">
                <section className="bg-muted/50 border-border rounded-2xl border p-4">
                    <Eyebrow as="span">{t("admin.alumni.contactLog.detail.note")}</Eyebrow>
                    <Text className={cn("mt-2 whitespace-pre-line", !contact.note && "text-text-caption")}>
                        {contact.note ?? t("admin.alumni.contactLog.noNote")}
                    </Text>
                </section>
                <section className="border-border flex items-start gap-3 rounded-2xl border p-4">
                    <CalendarDays className="text-text-caption mt-0.5 h-5 w-5 shrink-0" aria-hidden />
                    <div className="min-w-0">
                        <Text weight="semibold" className="text-text-heading">
                            {contact.followUpDate
                                ? t("admin.alumni.contactLog.detail.followUp", {
                                      date: formatCalendarDate(contact.followUpDate),
                                  })
                                : t("admin.alumni.contactLog.detail.noFollowUp")}
                        </Text>
                        <Text size="sm" muted className="mt-0.5">
                            {contact.followUpDate
                                ? t("admin.alumni.contactLog.detail.followUpHint", { name: client.firstName })
                                : t("admin.alumni.contactLog.detail.noFollowUpHint", { name: client.firstName })}
                        </Text>
                    </div>
                </section>
                <Text size="xs" className="text-text-caption">
                    {contact.contactedBy
                        ? t("admin.alumni.contactLog.detail.logged", {
                              date: formatDay(new Date(contact.loggedAt)),
                              name: contact.contactedBy,
                          })
                        : t("admin.alumni.contactLog.detail.loggedByRemoved", {
                              date: formatDay(new Date(contact.loggedAt)),
                          })}
                </Text>
            </div>

            {/* Only for whoever may change it — who logged it, or platform staff. The server checks too. */}
            {contact.canEdit ? (
                <DialogFooter className="items-center sm:justify-between">
                    <Button
                        variant="outline"
                        className="text-destructive hover:text-destructive w-auto"
                        onClick={onDelete}
                    >
                        {t("common.actions.delete")}
                    </Button>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row">
                        <Button
                            variant="secondary"
                            leftIcon={<FilePenLine className="h-4 w-4" aria-hidden />}
                            onClick={onEdit}
                        >
                            {t("admin.alumni.contactLog.detail.edit")}
                        </Button>
                    </div>
                </DialogFooter>
            ) : null}
        </>
    );
}

function EditContact({
    entry,
    today,
    onCancel,
    onSave,
}: {
    entry: ContactLogDialogEntry;
    today: Date;
    onCancel: () => void;
    onSave: (edited: LoggedContact) => Promise<void>;
}) {
    const { t } = useTranslation();
    const { contact, client } = entry;
    const summary = useSummary(entry);

    return (
        <>
            <DialogHeader>
                <DialogTitle>{t("admin.alumni.contactLog.detail.editTitle")}</DialogTitle>
                <DialogDescription>{summary}</DialogDescription>
            </DialogHeader>
            <LogContactForm
                layout="dialog"
                today={today}
                clientId={client.id}
                initial={{
                    channel: contact.channel,
                    outcome: contact.outcome,
                    note: contact.note,
                    followUpOn: contact.followUpDate ? parseCalendarDate(contact.followUpDate) : undefined,
                }}
                submitLabel={t("admin.alumni.contactLog.detail.save")}
                onCancel={onCancel}
                onSubmit={onSave}
            />
        </>
    );
}
